import React from 'react';

import { useCreateStream, useGetStream } from '@vlabs/api/artemy/client';
import type { CreateStreamRequest } from '@vlabs/api/artemy/model';
import { DuplicateIcon } from '@vlabs/icons';
import Loader from '@vlabs/shared/legacy-components/loader/Loader';
import { Tooltip, useModal } from '@vlabs/uikit';
import type { IconButtonProps } from '@vlabs/uikit/components/controls/button/icon-button';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { toast } from 'react-toastify';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { DuplicateStreamForm } from './DuplicateStreamForm';
import { useDuplicateStreamTranslation } from '../i18n';
import type { DuplicateStreamProps } from '../types';

const displayName = 'DuplicateStreamIconButton';
const testid = buildTestid(displayName);

interface DuplicateStreamIconButtonProps extends DuplicateStreamProps, WithDataTestid, IconButtonProps {}

function DuplicateStreamIconButton({
  streamId,
  groupId,
  onCreateStream = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: DuplicateStreamIconButtonProps): React.JSX.Element {
  const { t } = useDuplicateStreamTranslation();
  const modal = useModal({ size: 'small', scrollable: true });
  const getStream = useGetStream(streamId, { enriched: 0 }, {
    query: { enabled: modal.isOpen },
  });
  const createStream = useCreateStream();

  const onSubmit = async (data: CreateStreamRequest): Promise<void> => {
    try {
      const { stream_id: createdStreamId } = await createStream.mutateAsync({ data });

      modal.close();
      toast.success(t('on_success'));
      onCreateStream?.(createdStreamId);
    } catch {
      toast.error(t('on_error'));
    }
  };

  let modalContent: React.JSX.Element = <Loader />;
  if (!getStream.isLoading && getStream.data) {
    modalContent = (
      <DuplicateStreamForm
        groupId={groupId}
        isPending={createStream.isPending}
        onCancel={modal.close}
        onSubmit={onSubmit}
        stream={getStream.data}
        streamId={streamId}
      />
    );
  }

  return (
    <React.Fragment>
      {modal.wrap(modalContent)}

      <Tooltip tooltipContent={t('title')}>
        <IconButton
          icon={<DuplicateIcon />}
          kind="secondary"
          onClick={modal.open}
          variant="flat"
          {...testid(dataTestId)}
          {...props}
        />
      </Tooltip>
    </React.Fragment>
  );
}

DuplicateStreamIconButton.displayName = displayName;

export { DuplicateStreamIconButton };
